import React from 'react';
import {Images} from 'utils';
import {SetupGuideDrawer} from './setupGuideDrawer';
import {GuideStep, GuideCallout, GuideSection, GuideImage, GuideLinkButton} from './guideParts';

/**
 * Availity setup guide (Figma node 34970-45120).
 *
 * Distinct from the Office Ally guide: Step 2 ("Create your FTP/SFTP credentials")
 * walks through the Availity portal in nested sub-steps, each with its own
 * screenshot. Inline screenshots are placeholder slots (`GuideImage` with no
 * `src`) — export them from Figma into `assets/images/guide/` and pass as `src`.
 */
export const AvailityGuide = ({open, onClose}) => {
    return (
        <SetupGuideDrawer open={open} onClose={onClose} title="Connect Availity to TherapyLake">
            <p className="guide-intro">
                Use your Availity FTP/SFTP mailbox credentials to securely exchange claim and response files between
                Availity and TherapyLake.
            </p>

            <GuideCallout tone="info" title="Before you begin">
                You&apos;ll need an active Availity account with access to Claims &amp; Payments and permission to
                manage
                FTP/SFTP connection services. Your regular Availity login credentials may not be the same credentials
                used for your FTP/SFTP mailbox.
            </GuideCallout>

            <GuideStep index="1" title="Set up Availity" subtitle="Make sure your organization is ready for EDI">
                <p>
                    Sign in to Availity Essentials, or create an Availity account if your organization doesn&apos;t
                    already have one.
                </p>
                <p>
                    Before setting up the connection, confirm that the payers you plan to bill are available through
                    Availity and that your organization has the appropriate EDI access.
                </p>
                <GuideLinkButton href="https://www.availity.com" icon={Images.externalLink} external>
                    Open Availity
                </GuideLinkButton>
                <p className="guide-note">
                    Some payers or transaction types may require additional enrollment or an Availity EDI plan.
                </p>
            </GuideStep>

            <GuideStep index="2" title="Create your FTP/SFTP credentials">
                <p className="guide-ol-step">Step 1: Log in to the Availity portal</p>
                <ol className="guide-list guide-list--ordered">
                    <li>Sign in to the Availity portal.</li>
                    <li>Navigate to Claims &amp; Payments.</li>
                    <li>Select FTP - EDI Connection Services.</li>
                </ol>
                <GuideImage caption="Claims & Payments → FTP - EDI Connection Services" src={Images.availityWeb}
                            alt='avalityWeb'/>

                <p className="guide-ol-step">Step 2: Access the FTP Mailbox Settings</p>
                <p>Click Manage Your FTP Mailbox.</p>
                <GuideImage caption="FTP and EDI Connection Services → Manage Your FTP Mailbox" src={Images.ftpAndEdi}
                            alt='ftpAndEdi'/>

                <p className="guide-ol-step">Step 3: Select the Organization</p>
                <p>Choose the organization for which you want to create the FTP connection.</p>
                <GuideImage caption="Manage Your FTP Mailbox → select organization" src={Images.manageFtp}
                            alt='manageFtp'/>

                <p className="guide-ol-step">Step 4: Create FTP Credentials</p>
                <p>Create the FTP credentials (username and password) for the mailbox.</p>
                <GuideCallout tone="info">
                    <b>Important:</b> These credentials will be required when configuring the <b>SFTP connection in
                    TherapyLake.</b> They should be <b>different from the credentials used to log in to the Availity
                    portal.</b>
                </GuideCallout>
                <GuideImage caption="Manage Your FTP Mailbox → create FTP credentials" src={Images.createAccountAvaility}
                            alt='createAccountAvaility'/>

                <p className="guide-ol-step">Step 5: Save the Configuration</p>
                <p>
                    Once completed, you should receive a <b>confirmation message indicating that your FTP mailbox has
                    been successfully created.</b>
                </p>
                <GuideCallout tone="info">
                    You can now use these credentials to <b>complete the SFTP configuration in TherapyLake.</b>
                </GuideCallout>
            </GuideStep>

            <GuideStep index="3" title="Connect Availity in TherapyLake" subtitle="Enter your FTP/SFTP credentials">
                <p><b>Enter your Availity credentials</b></p>
                <p>
                    Enter the FTP/SFTP mailbox credentials you created in Availity, then click <b>Save</b> to connect
                    Availity.
                </p>
                <ul className="guide-list">
                    <li>Username</li>
                    <li>Password</li>
                </ul>
                <GuideImage caption="Integrations → Availity → Connect" src={Images.availityIntegrated} alt='availityIntegrated'/>
            </GuideStep>

            <GuideStep index="4" title="Configure file processing"
                       subtitle="Choose how TherapyLake handles SFTP files">
                <p>
                    The Availity connection includes an option that allows TherapyLake to automatically manage files in the configured SFTP folder after processing.
                </p>
                <p>
                    Enable it if TherapyLake should manage those files as part of the integration workflow. It’s the toggle shown beneath the password field in the form above.
                </p>
                <GuideCallout tone="warning" icon={Images.alertWarning}>
                    Before enabling this option, make sure the configured SFTP folder is intended for TherapyLake
                    processing and does not contain files that need to remain unchanged.
                </GuideCallout>
            </GuideStep>

            <GuideStep index="5" title="Review EDI settings"
                       subtitle="Make sure response files can be returned correctly">
                <p>
                    After connecting Availity, review your EDI configuration in Availity to make sure the claim response
                    files your workflow relies on are enabled.
                </p>
                <p>In Availity Essentials:</p>
                <ol className="guide-list guide-list--ordered">
                    <li>Go to Claims &amp; Payments.</li>
                    <li>Open EDI Reporting Preferences.</li>
                    <li>Select your organization.</li>
                    <li>Review and save the reporting preferences required for your EDI workflow.</li>
                </ol>
                <GuideCallout
                    tone="info"
                    icon={Images.infoCircle}
                    imageStyles={{
                        filter: 'brightness(0) saturate(100%) invert(27%) sepia(97%) saturate(2081%) hue-rotate(209deg) brightness(89%) contrast(95%)',
                        width: 16,
                        height: 16,
                    }}
                >
                    Some payer enrollments or EDI transactions may require additional setup in Availity before claims
                    and
                    response files can be exchanged successfully.
                </GuideCallout>
            </GuideStep>

            <GuideSection title="Complete the connection">
                <p>
                    Click Save after entering your credentials. Once your FTP/SFTP credentials are validated
                    successfully, Availity will appear as connected on the Integrations page.
                </p>
                <GuideCallout tone="success" icon={Images.isReadyGreen}>
                    Availity is ready to use once your FTP/SFTP credentials have been validated successfully.
                </GuideCallout>
            </GuideSection>

            <GuideSection title="Having trouble connecting?">
                <ul className="guide-list">
                    <li>Make sure you're using the credentials for your Availity FTP/SFTP mailbox.</li>
                    <li>Check that the username and password were entered correctly.</li>
                    <li>Confirm that your organization has access to FTP and EDI Connection Services in Availity.</li>
                    <li>Confirm that your required payer/EDI enrollment is complete.</li>
                    <li>If you cannot create or manage the FTP mailbox, contact your Availity administrator or Availity support.</li>
                </ul>
            </GuideSection>
        </SetupGuideDrawer>
    );
};
